import { Link } from "wouter";
import { ArrowRight, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { MarketingSection, MarketingSectionHeader } from "@/components/marketing";
import { INSIGHTS_META as INSIGHTS, type InsightMeta as InsightArticle } from "@/content/insightsMeta";

interface RelatedInsightsProps {
  maxItems?: number;
  title?: string;
  excludeSlug?: string;
}

export default function RelatedInsights({ 
  maxItems = 3, 
  title = "Related Insights",
  excludeSlug 
}: RelatedInsightsProps) {
  const articles = excludeSlug 
    ? INSIGHTS.filter(a => a.slug !== excludeSlug).slice(0, maxItems)
    : INSIGHTS.slice(0, maxItems);

  if (articles.length === 0) {
    return null;
  }

  return (
    <MarketingSection id="related-insights" maxWidth="4xl" background="muted">
      <MarketingSectionHeader title={title} />
      <div className="grid md:grid-cols-3 gap-4">
        {articles.map((article, i) => (
          <Link key={article.slug} href={`/insights/${article.slug}`}>
            <Card className="bg-slate-900/50 border-slate-700/50 hover:border-teal-500/50 transition-colors cursor-pointer group h-full" data-testid={`card-related-insight-${i}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                  <span className="bg-teal-500/10 text-teal-400 px-2 py-0.5 rounded text-[10px]">{article.category}</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {article.readingTime}
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-white group-hover:text-teal-400 transition-colors line-clamp-2 mb-2">
                  {article.title}
                </h3>
                <p className="text-xs text-slate-400 line-clamp-2 mb-3">{article.description}</p>
                <div className="flex items-center text-teal-400 text-xs font-medium">
                  Read article <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </MarketingSection>
  );
}
